/**
 * Gamification API Service
 * USER-CON-001: Points, Badges, Streaks, and Tiers
 *
 * Handles all gamification-related API calls
 */

import { fetchAPI } from './api.js';

// ============================================================================
// POINTS
// ============================================================================

/**
 * Get user's points summary
 */
export async function getPointsSummary() {
  return fetchAPI('/gamification/points');
}

/**
 * Get point transaction history
 */
export async function getPointHistory(options = {}) {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.per_page) params.append('per_page', options.per_page);
  if (options.action_type) params.append('action_type', options.action_type);

  const query = params.toString();
  return fetchAPI(`/gamification/points/history${query ? `?${query}` : ''}`);
}

// ============================================================================
// BADGES
// ============================================================================

/**
 * Get user's earned badges
 */
export async function getUserBadges() {
  // /gamification/badges is the CATALOG of every badge (List[BadgeResponse]).
  // The user's EARNED badges are /gamification/badges/my
  // (UserBadgeListResponse = { items, total }). This function's own name and
  // docstring promise the latter and were fetching the former, so the dashboard
  // showed every badge in existence as if the user had earned them.
  //
  // The two endpoints return DIFFERENT SHAPES -- an array versus an object --
  // and GamificationDashboard does `badges.slice(0, 4)` and `badges.length`,
  // so swapping the path alone would hand it an object and break the panel.
  // Unwrap to the array its consumers already expect.
  const response = await fetchAPI('/gamification/badges/my');
  return response?.items ?? [];
}

/**
 * Get badge progress for unearned badges
 */
export async function getBadgeProgress() {
  return fetchAPI('/gamification/badges/progress');
}

/**
 * Get public badge catalog
 */
export async function getBadgeCatalog() {
  return fetchAPI('/gamification/badges/catalog');
}

/**
 * Set a badge as featured on profile
 */
export async function setFeaturedBadge(badgeId, featured = true) {
  return fetchAPI(`/gamification/badges/${badgeId}/feature`, {
    method: 'PUT',
    body: JSON.stringify({ is_featured: featured }),
  });
}

// ============================================================================
// STREAKS
// ============================================================================

/**
 * Get streak information
 */
export async function getStreakInfo() {
  return fetchAPI('/gamification/streak');
}

/**
 * Record daily activity (if not already recorded)
 */
export async function recordDailyActivity() {
  // SERVED path is /streak/record. /streak/checkin has never existed on the API, so the
  // dashboard's daily check-in 404'd on every click; the caller's catch logged and moved on,
  // so the button appeared to work and the streak silently never advanced.
  return fetchAPI('/gamification/streak/record', {
    method: 'POST',
  });
}

// ============================================================================
// TIERS
// ============================================================================

/**
 * Get user's tier information
 */
export async function getTierInfo() {
  return fetchAPI('/gamification/tier');
}

/**
 * Get available tier discounts
 */
export async function getTierDiscounts() {
  return fetchAPI('/gamification/tier/discounts');
}

// ============================================================================
// FLASH REWARDS
// ============================================================================

/**
 * Get active flash rewards
 */
export async function getFlashRewards() {
  // SERVED path is /rewards (FlashRewardListResponse). Two things were wrong, and fixing only
  // the first would have swapped a blank panel for a crash: the endpoint returns
  // { items, claimable_count, expired_count }, while every consumer treats this as an ARRAY
  // (GamificationDashboard holds it in state and calls .filter/.map on it). Unwrapped here so
  // the shape the callers already assume is the shape they get.
  const data = await fetchAPI('/gamification/rewards');
  return data?.items ?? [];
}

/**
 * Claim a flash reward
 */
export async function claimFlashReward(rewardId) {
  // SERVED path is POST /rewards/claim, which takes the id in the BODY (ClaimRewardRequest),
  // not in the path.
  return fetchAPI('/gamification/rewards/claim', {
    method: 'POST',
    body: JSON.stringify({ reward_id: rewardId }),
  });
}

// ============================================================================
// LEADERBOARD
// ============================================================================

/**
 * Get points leaderboard
 */
export async function getLeaderboard(options = {}) {
  const params = new URLSearchParams();
  if (options.period) params.append('period', options.period); // 'weekly', 'monthly', 'all_time'
  if (options.limit) params.append('limit', options.limit);

  const query = params.toString();
  return fetchAPI(`/gamification/leaderboard${query ? `?${query}` : ''}`);
}

// ============================================================================
// CONVENTIONS
// ============================================================================

/**
 * Get upcoming conventions (public)
 */
export async function getConventions(options = {}) {
  const params = new URLSearchParams();
  if (options.state) params.append('state', options.state);
  if (options.days_ahead) params.append('days_ahead', options.days_ahead);
  if (options.has_guests !== undefined) params.append('has_guests', options.has_guests);

  const query = params.toString();
  return fetchAPI(`/conventions${query ? `?${query}` : ''}`);
}

/**
 * Get convention details
 */
export async function getConventionDetail(conventionId) {
  return fetchAPI(`/conventions/${conventionId}`);
}

/**
 * Get convention guest list
 */
export async function getConventionGuests(conventionId) {
  return fetchAPI(`/conventions/${conventionId}/guests`);
}

/**
 * Get high-impact creators at convention
 */
export async function getHighImpactCreators(conventionId) {
  return fetchAPI(`/conventions/${conventionId}/high-impact`);
}

// ============================================================================
// ATTENDANCE PLANNING
// ============================================================================

/**
 * Set attendance plan for a convention
 */
export async function setAttendancePlan(conventionId, status) {
  return fetchAPI(`/conventions/${conventionId}/attend`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
}

/**
 * Get attendance plans
 */
export async function getMyAttendancePlans() {
  return fetchAPI('/conventions/my-plans');
}

/**
 * Get planned books for a convention
 */
export async function getPlannedBooks(conventionId) {
  return fetchAPI(`/conventions/${conventionId}/books`);
}

/**
 * Add book to convention plan
 */
export async function addPlannedBook(conventionId, bookData) {
  return fetchAPI(`/conventions/${conventionId}/books`, {
    method: 'POST',
    body: JSON.stringify(bookData),
  });
}

/**
 * Remove book from convention plan
 */
export async function removePlannedBook(conventionId, bookId) {
  return fetchAPI(`/conventions/${conventionId}/books/${bookId}`, {
    method: 'DELETE',
  });
}

/**
 * Check in at convention
 */
export async function checkInAtConvention(conventionId) {
  return fetchAPI(`/conventions/${conventionId}/checkin`, {
    method: 'POST',
  });
}

// ============================================================================
// SUBMISSION TRACKING
// ============================================================================

/**
 * Get user's submission tracking records
 */
export async function getSubmissions(options = {}) {
  const params = new URLSearchParams();
  if (options.status) params.append('status', options.status);
  if (options.grading_company) params.append('grading_company', options.grading_company);

  const query = params.toString();
  return fetchAPI(`/submissions${query ? `?${query}` : ''}`);
}

/**
 * Create a new submission
 */
export async function createSubmission(submissionData) {
  return fetchAPI('/submissions', {
    method: 'POST',
    body: JSON.stringify(submissionData),
  });
}

/**
 * Update submission status
 */
export async function updateSubmission(submissionId, updates) {
  return fetchAPI(`/submissions/${submissionId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}
